import { useEffect, useRef, useState } from "react";
import { getSquarePayments } from "@/lib/squarePayments";

/**
 * SquareCardForm — renders Square Web Payments SDK card element.
 * Exposes an imperative `tokenize()` via ref.
 */
export default function SquareCardForm({ onReady, onError }) {
  const containerRef = useRef(null);
  const cardRef = useRef(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    let mounted = true;
    const appId = process.env.REACT_APP_SQUARE_APPLICATION_ID;
    const locationId = process.env.REACT_APP_SQUARE_LOCATION_ID;

    if (!appId || !locationId) {
      setStatus("error");
      onError && onError("Payment not configured");
      return;
    }

    (async () => {
      try {
        const payments = await getSquarePayments();
        if (!mounted) return;
        const card = await payments.card({
          style: {
            input: { fontSize: "16px", color: "#292524" },
            ".input-container": { borderRadius: "10px", borderColor: "#d6d3d1" },
            ".input-container.is-focus": { borderColor: "#9b2226" },
          },
        });
        await card.attach(containerRef.current);
        cardRef.current = card;
        setStatus("ready");
        onReady && onReady({
          tokenize: async (amountDollars, billingContact = {}) => {
            // Square Web Payments SDK requires verificationDetails.billingContact
            // for buyer verification (SCA / 3-D Secure).
            const [givenName = "Guest", ...rest] = String(billingContact.name || "").trim().split(/\s+/);
            const familyName = rest.join(" ");
            const verificationDetails = {
              amount: amountDollars.toFixed(2),
              currencyCode: "USD",
              intent: "CHARGE",
              customerInitiated: true,
              sellerKeyedIn: false,
              billingContact: {
                givenName,
                ...(familyName ? { familyName } : {}),
                ...(billingContact.phone ? { phone: billingContact.phone } : {}),
                ...(billingContact.email ? { email: billingContact.email } : {}),
                countryCode: "US",
              },
            };
            const result = await card.tokenize(verificationDetails);
            if (result.status !== "OK") {
              const err = (result.errors || []).map((e) => e.message).join(", ") || result.status;
              throw new Error(err);
            }
            return result.token;
          },
        });
      } catch (e) {
        if (mounted) {
          setStatus("error");
          onError && onError(e.message || "Payment form failed to load");
        }
      }
    })();

    return () => {
      mounted = false;
      if (cardRef.current && cardRef.current.destroy) {
        cardRef.current.destroy().catch(() => {});
      }
    };
  }, []);

  return (
    <div>
      <div
        ref={containerRef}
        data-testid="square-card-container"
        className={`min-h-[64px] rounded-lg border ${
          status === "ready" ? "border-transparent" : "border-stone-300 bg-white"
        }`}
      />
      {status === "loading" && (
        <div className="text-xs text-stone-400 mt-2 font-hand text-base">
          Loading secure card form...
        </div>
      )}
      {status === "error" && (
        <div className="text-xs text-brand-red mt-2">
          Payment form unavailable. Refresh and try again.
        </div>
      )}
    </div>
  );
}
